import { Button, Flex } from "@chakra-ui/react";
import React from "react";
import { RiAddLine } from "react-icons/ri";
import { StudentSelectInput } from "src/components/Inputs/StudentSelectInput";
import { Glossary } from "src/components/Text/Glossary";
import type { ApplicantDuplicate } from "src/generated/hooks/form-service/requests";
import { parseName } from "src/services/format";
import { NewStudentForm } from "./NewStudentForm";
import { StudentChip } from "./StudentChip";
import type { NewStudentEntry, StudentEntry } from "./useStudentEntries";

export interface StudentSectionProps {
  organizationId: string;
  entries: StudentEntry[];
  // Name of the parent the students belong to, for a new student's
  // "Child of {parent}" line.
  parentName: string;
  onAddExisting: (student: ApplicantDuplicate) => void;
  onAddDraft: (prefill?: { firstName?: string; lastName?: string }) => void;
  onUpdateDraft: (id: string, patch: Partial<NewStudentEntry>) => void;
  // Collapse a new draft into a read-only chip (still in the payload).
  onConfirmDraft: (id: string) => void;
  onRemove: (id: string) => void;
  // Student ids already linked to the parent, hidden from search results.
  excludeIds?: string[];
  disablePortal?: boolean;
}

type AddMode = "search" | "idle";

export function StudentSection({
  organizationId,
  entries,
  parentName,
  onAddExisting,
  onAddDraft,
  onUpdateDraft,
  onConfirmDraft,
  onRemove,
  excludeIds,
  disablePortal,
}: StudentSectionProps) {
  const [mode, setMode] = React.useState<AddMode>("search");

  // Students already linked to the parent (excludeIds) plus any already added in
  // this session. Shown disabled in search, never hidden, so they can't be re-added.
  const linkedIds = React.useMemo(
    () => [
      ...(excludeIds ?? []),
      ...entries
        .filter((entry) => entry.kind === "existing")
        .map((entry) => entry.id),
    ],
    [excludeIds, entries]
  );

  const handleSelectExisting = (student: ApplicantDuplicate) => {
    onAddExisting(student);
    setMode("idle");
  };

  const handleCreateNew = (searchValue: string) => {
    onAddDraft(parseName(searchValue));
    setMode("idle");
  };

  return (
    <Flex direction="column" gap={4} width="100%">
      {entries.map((entry) =>
        entry.kind === "new" && !entry.confirmed ? (
          <NewStudentForm
            key={entry.id}
            entry={entry}
            onChange={(patch) => onUpdateDraft(entry.id, patch)}
            onConfirm={() => onConfirmDraft(entry.id)}
            onRemove={() => onRemove(entry.id)}
          />
        ) : (
          <StudentChip
            key={entry.id}
            entry={entry}
            parentName={parentName}
            onRemove={onRemove}
          />
        )
      )}

      {mode === "search" && (
        <StudentSelectInput
          organizationId={organizationId}
          excludeIds={linkedIds}
          disablePortal={disablePortal}
          onSelectedStudent={(student) => {
            if (student) handleSelectExisting(student);
          }}
          onCreateNew={handleCreateNew}
        />
      )}

      {mode === "idle" && (
        <Button
          type="button"
          variant="outline"
          colorScheme="gray"
          alignSelf="flex-start"
          leftIcon={<RiAddLine />}
          onClick={() => setMode("search")}
        >
          <Glossary>Add another student</Glossary>
        </Button>
      )}
    </Flex>
  );
}
